import { Avatar, Box, HStack, Icon } from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import CDS from "design-system";
import { Select } from "chakra-react-select";
import type { OptionProps } from "chakra-react-select";
import type { SelectInstance } from "react-select";
import { FiCheck } from "react-icons/fi";
import apiClient from "services/ApiClient";
import { User } from "features/User/models";
import { Task } from "features/Resources/Task/models";
import API_ROUTES from "definitions/constants/routes/api";

type UserOption = { value: string; label: string; avatarUrl?: string | null };

function AssigneeOption({ data, isSelected, isFocused, innerRef, innerProps }: OptionProps<UserOption>) {
  return (
    <Box
      ref={innerRef}
      {...innerProps}
      px={3}
      py="6px"
      bg={isFocused ? "gray.50" : "transparent"}
      cursor="pointer"
    >
      <HStack spacing={2} align="center">
        <Avatar name={data.label} src={data.avatarUrl ?? undefined} size="xs" bg="cdsIndigo.300" color="white" flexShrink={0} />
        <CDS.Text12 flex={1} color="content.primary" noOfLines={1}>
          {data.label}
        </CDS.Text12>
        {isSelected && <Icon as={FiCheck} color="cdsIndigo.500" boxSize={3} flexShrink={0} />}
      </HStack>
    </Box>
  );
}

interface AssigneeSelectProps {
  assignee: Task["assignee"];
  onSelect: (id: string) => void;
}

export default function AssigneeSelect({ assignee, onSelect }: AssigneeSelectProps) {
  const selectRef = useRef<SelectInstance<UserOption>>(null);
  const [options, setOptions] = useState<UserOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  const fetchUsers = useCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        const users = await apiClient.get<User[]>(
          `${API_ROUTES.users.search}?q=${encodeURIComponent(query)}&per_page=20`,
        );
        const mapped = users.map((u) => ({ value: u.id, label: u.fullName ?? "", avatarUrl: u.avatarUrl }));
        if (assignee) {
          const idx = mapped.findIndex((o) => o.value === assignee.id);
          if (idx > 0) {
            const [current] = mapped.splice(idx, 1);
            mapped.unshift(current);
          }
        }
        setOptions(mapped);
      } finally {
        setIsLoading(false);
      }
    },
    [assignee],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchUsers("");
  }, [fetchUsers]);

  const currentValue: UserOption | null = assignee ? { value: assignee.id, label: assignee.fullName ?? "" } : null;

  return (
    <Select<UserOption>
      ref={selectRef as React.Ref<SelectInstance<UserOption>>}
      options={options}
      isLoading={isLoading}
      value={currentValue}
      filterOption={() => true}
      menuIsOpen
      onInputChange={(val, { action }) => {
        if (action === "input-change") fetchUsers(val);
      }}
      onChange={(opt) => {
        if (opt) onSelect(opt.value);
      }}
      placeholder="Search users..."
      controlShouldRenderValue={false}
      size="sm"
      useBasicStyles
      components={{ DropdownIndicator: null, Option: AssigneeOption }}
    />
  );
}
